/**
 * The module catalogue: what each licence checkbox actually opens.
 *
 * Lifted out of LicensesPage so the licence editor and the per-user rights
 * picker describe a module with the same words — the alternative was a second
 * hand-typed list, which is exactly how "Actifs" and "Engins" came to be two
 * names for one thing.
 */

// [code, label, what ticking it actually opens].
//
// The third entry exists because one-word labels were not decidable: "Actifs"
// read as an adjective or as a balance-sheet term rather than as the machines
// that burn the fuel, and "Livraisons" pointed at deliveries to a customer
// when the module records fuel arriving from a supplier. Whoever sells a
// licence has to know what they are switching on.
import { libelle } from './libelles';

export const MODULE_GROUPS = [
  {
    label: 'Transport & distribution',
    description: 'Acheminer du carburant pour le compte d’un donneur d’ordre.',
    modules: [
      // "entretien" moved out of this line when the maintenance module landed:
      // two checkboxes claiming the same word made the licence undecidable.
      ['trucks', 'Camions', 'Parc, matricules, capacités, rattachement société.'],
      ['drivers', 'Chauffeurs', 'Fiches, permis, affectation aux trajets.'],
      [
        'maintenance',
        'Maintenance',
        'Compteurs, entretiens préventifs, ordres de travail, pannes, échéances assurance et visite technique. Un camion immobilisé ici ne peut plus être affecté à un trajet.',
      ],
      [
        'truck_rentals',
        'Location de camions',
        'Louer ses citernes à un tiers : contrat, tarif à la période, à la rotation ou au litre, avec ou sans chauffeur, état des lieux et restitution. Un camion loué sans chauffeur sort de la flotte et ne peut plus partir en trajet.',
      ],
      ['operations', 'Convois', 'Regroupe plusieurs trajets sous un même départ.'],
      [
        'lots',
        'Lots attribués',
        'Camions chargés achetés à un transporteur partenaire à un prix au litre unique : attribution des plaques, confirmation par étape, suivi et retour des camions.',
      ],
      ['trips', 'Trajets', 'Cycle de vie complet, de la création à la clôture.'],
      ['dispatch_allocations', 'Répartition', 'Affecte le volume d’une commande aux camions disponibles.'],
      ['workflow_engine', 'Parcours du trajet', 'Définit les étapes qu’un trajet peut suivre, et dans quel ordre.'],
      ['tracking', 'Suivi en direct', 'Carte temps réel et alertes de sortie de zone.'],
      ['gps_tracking', 'Boîtiers GPS', 'Intègre les traceurs matériels installés sur les véhicules.'],
      ['clients', 'Clients', 'Encours, plafond de crédit, prix de référence.'],
      ['orders', 'Commandes', 'Commande client : produit, volume, lieu, prix.'],
      ['companies', 'Sociétés partenaires', 'Transporteurs et donneurs d’ordre tiers.'],
      ['invoices', 'Factures', 'Facturation sur les volumes réellement livrés.'],
      ['payments', 'Encaissements', 'Règlements, relances et litiges.'],
      ['volume_reconciliation', 'Réconciliation', 'Compare le volume chargé au volume livré.'],
    ],
  },
  {
    label: 'Stations',
    description: 'Ouvre les écrans d’exploitation station et réseau multi-stations.',
    modules: [
      ['stations', 'Stations', 'Les points de vente du réseau.'],
      ['tanks', 'Cuves station', 'Cuves du point de vente et niveaux.'],
      ['pumps', 'Pompes', 'Pistolets et index de compteur.'],
      ['prices', 'Prix station', 'Prix de vente par produit et historique.'],
      ['station_pos', 'Caisse', 'Encaissement des ventes au comptoir.'],
      ['station_sales', 'Ventes', 'Ventes carburant par pompe et par quart.'],
      ['purchases', 'Achats', 'Approvisionnements reçus par la station.'],
      ['shifts', 'Postes', 'Ouverture et clôture de poste, écarts de caisse.'],
      ['attendants', 'Pompistes', 'Personnel affecté aux pompes.'],
      ['station_expenses', 'Dépenses', 'Charges courantes de la station.'],
      ['station_kpis', 'Indicateurs station', 'Marge, rotation et débit par pompe.'],
      ['station_daily_report', 'Rapport journalier', 'Clôture quotidienne consolidée.'],
      ['multi_station_dashboard', 'Tableau de bord réseau', 'Vue consolidée de toutes les stations.'],
      ['station_credit', 'Clients et bons', 'Comptes crédit, flottes, bons carburant et relevés mensuels des clients de la station.'],
      ['station_annexes', 'Activités annexes', 'Gaz, lubrifiants, boutique et prestations (lavage, vidange).'],
      ['volume_reconciliation', 'Réconciliation', 'Compare le stock théorique au volume jaugé.'],
    ],
  },
  {
    label: 'Contrôle',
    description:
      'Pour une société qui consomme son propre carburant — mine, BTP, agro — au lieu de le vendre ou de le transporter.',
    modules: [
      ['sites', 'Sites', 'Chantiers, mines ou dépôts où le carburant est consommé.'],
      // "Engins" and not "Actifs": that is the word the sidebar already uses
      // for this module, and the two screens naming the same thing differently
      // is what made the picker unreadable.
      ['assets', 'Engins', 'Les machines et véhicules qui consomment le carburant, et ce qui est attribué à chacun.'],
      ['fuel_requests', 'Demandes de carburant', 'Demande d’un site ou d’un chef d’équipe, à approuver.'],
      ['fuel_issues', 'Sorties de cuve', 'Carburant servi à un engin : qui, combien, quand.'],
      ['fuel_quotas', 'Quotas', 'Plafond de litres par site, engin ou période.'],
      ['fuel_consumption', 'Consommation', 'Litres aux 100 km ou à l’heure, par engin.'],
      ['volume_reconciliation', 'Réconciliation', 'Compare le stock théorique au volume jaugé.'],
    ],
  },
  {
    label: 'Transversal',
    description: 'Fonctions communes aux trois packs, visibles seulement si cochées.',
    modules: [
      ['stock_movements', 'Mouvements de stock', 'Entrées et sorties de volume, quel que soit le pack.'],
      ['documents', 'Documents et lecture automatique', 'Pièces jointes et lecture automatique des reçus.'],
      ['alerts', 'Alertes', 'Anomalies métier et notifications.'],
      ['reports', 'Rapports', 'Exports et rapports périodiques.'],
      ['finance_cockpit', 'Finance', 'Recettes, dépenses et marges consolidées.'],
      ['pricing_rules', 'Règles tarifaires', 'Barèmes appliqués automatiquement.'],
      ['offline_sync', 'Continuité de service', 'Les écrans et les saisies restent utilisables à tout moment.'],
      ['advanced_audit', 'Audit avancé', 'Journal opposable des actions sensibles.'],
      ['external_integrations', 'Intégrations externes', 'Connecteurs tiers : WhatsApp, GPS, ERP.'],
    ],
  },
];
// Vendus, jamais construits: ni routeur, ni modèle, ni écran derrière la
// case. Ils sortent des groupes ci-dessus pour ne plus être cochables, mais
// gardent un libellé — des licences signées avant ce retrait les portent
// encore, et l'opérateur qui doit décider de les décocher a besoin de lire
// autre chose qu'un code snake_case.
//
// [code, label, pourquoi il ne se vend plus].
export const RETIRED_MODULES = [
  ['internal_tanks', 'Cuves internes', 'Aucun écran ne s’ouvre : décochez.'],
  ['supplier_deliveries', 'Réceptions fournisseur', 'Aucun écran ne s’ouvre : décochez.'],
  ['api', 'Accès externe aux données', 'Aucun écran ne s’ouvre : décochez.'],
];

// The transport modules had no entry at all, so moduleLabel fell through to
// its raw-code fallback and every Transport licence listed "trucks",
// "gps_tracking" and a dozen other snake_case codes to the operator.
export const MODULE_LABELS = Object.fromEntries([
  ...MODULE_GROUPS.flatMap((group) => group.modules.map(([code, label]) => [code, label])),
  ...RETIRED_MODULES.map(([code, label]) => [code, label]),
]);

// Un module inconnu ne montre jamais son code brut.
export const moduleLabel = (code) =>
  MODULE_LABELS[code] || libelle('module', code, 'Module à préciser');

export const MODULE_HELP = Object.fromEntries(
  MODULE_GROUPS.flatMap((group) =>
    group.modules.filter(([, , help]) => help).map(([code, , help]) => [code, help]),
  ),
);
